import React, { useContext } from 'react';
import styled from 'styled-components';
import { Title, Icon, Text, Tooltip, Divider, Button, Modal } from 'components';
import { AuthContext } from 'contexts';
import {
  checkUserBillingPermission,
  formatCurrency,
  hasValidStripeMlsProductId,
  makeModalResponsive,
} from 'utils';
import { StyledIconWrapper, StyledActivateSection } from '../StyledComponents';
import { pricingTierCalculator } from '../util';

import AppointmentDeskContext from '../AppointmentDeskContext';

interface YourPlanInfoProps {
  isConfirmPage?: boolean;
  isMlsCovered?: boolean;
}

const YourPlanInfo: React.FC<YourPlanInfoProps> = ({
  isConfirmPage,
  isMlsCovered,
}) => {
  const {
    owner,
    answeringSubscription,
    portalConfig,
    subscriptionType,
    billingRedirection,
    isBoardCovered,
    association,
    enhancedPrices,
    enabledListingsCount,
  } = useContext(AppointmentDeskContext);
  const { auth } = useContext(AuthContext);

  if (!portalConfig) return null;

  const { answeringServiceName, brokeragePrice, agentPrice } = portalConfig;
  const subscriptionLevel =
    answeringSubscription?.subscriptionLevel || subscriptionType;

  const TOOLTIP_TEXT = isMlsCovered
    ? 'The MLS is covering the cost of the Live Concierge Service, you will not be charged.'
    : isBoardCovered
      ? 'The association is covering the cost of the Live Concierge Service, you will not be charged.'
      : 'Your monthly price will update as you add / remove listings from the Live Concierge Service.';

  let priceText =
    subscriptionLevel === 'org'
      ? `${formatCurrency(brokeragePrice / 100)}/mo/listing`
      : `${formatCurrency(agentPrice / 100)}/mo`;
  if (isMlsCovered || isBoardCovered || answeringSubscription?.paidOffline) {
    priceText = '$0';
  }

  if (
    hasValidStripeMlsProductId(portalConfig.mlsProductId) &&
    !answeringSubscription?.paidOffline
  ) {
    if (association?.useLiveConciergePlus) {
      priceText = '';
    } else if (enhancedPrices.length) {
      priceText = `${formatCurrency(
        pricingTierCalculator(enhancedPrices, enabledListingsCount),
      )}/mo`;
    }
  }

  const priceInfo = (
    <>
      <StyledPriceText data-cy="price-text">{priceText}</StyledPriceText>
      {subscriptionLevel === 'org' &&
        !hasValidStripeMlsProductId(portalConfig.mlsProductId) && (
          <StyledTooltipWrapper>
            <Tooltip placement="right" title={TOOLTIP_TEXT}>
              <StyledIcon>
                <Icon type="antdQuestionCircleFilled" color="gray6" />
              </StyledIcon>
            </Tooltip>
          </StyledTooltipWrapper>
        )}
    </>
  );

  let serviceInfo =
    auth?.org.onboardingPortalId &&
    (process.env.PORTAL_IDS_USING_SUPRA_CALL || '')
      .split(',')
      .includes(auth.org.onboardingPortalId)
      ? // TODO: This Timezone of CST should change if other timezoned MLS also
        // subscribes to Supra Call Center. Need Product team's work on wording change if that happens.
        'Live 7AM - 9PM CST service, 7 days a week'
      : '24 / 7 / 365 Live concierge service';

  if (hasValidStripeMlsProductId(portalConfig.mlsProductId)) {
    if (
      !association?.useAppointmentDesk &&
      !association?.useLiveConciergePlus &&
      !isConfirmPage
    ) {
      serviceInfo = '';
    } else {
      serviceInfo = '24 / 7 / 365 Live Answering';
    }
  }

  const canAccessBilling = () => {
    const userCanAccessBilling =
      subscriptionLevel !== 'org' || checkUserBillingPermission(auth);

    if (!userCanAccessBilling) {
      const { firstName, lastName, email } = owner || {};
      makeModalResponsive(
        Modal.error({
          title: 'You are not authorized to manage billing',
          content: owner
            ? `You must ask the account owner (${firstName} ${lastName}, ${email}) for permission.`
            : 'You must ask the account owner for permission.',
          centered: true,
        }),
      );
    } else {
      billingRedirection();
    }
  };

  return (
    <>
      <Title level={3} data-cy="your-plan-title">
        Your Plan
      </Title>
      <StyledActivateSection $border>
        <StyledIconWrapper>
          <Icon type="antdPhoneFilled" color="info" />
        </StyledIconWrapper>
        <StyledPlanDetails>
          <StyledAnsweringName data-cy="answering-service-name">
            {hasValidStripeMlsProductId(portalConfig.mlsProductId)
              ? 'Live Concierge'
              : answeringServiceName}
          </StyledAnsweringName>
          <StyledServiceInfo type="secondary">
            {isConfirmPage &&
            subscriptionType === 'org' &&
            (!hasValidStripeMlsProductId(portalConfig.mlsProductId) ||
              !association?.useLiveConciergePlus)
              ? priceInfo
              : serviceInfo}
          </StyledServiceInfo>
        </StyledPlanDetails>
        {subscriptionLevel === subscriptionType && (
          <StyledPriceInfo>
            {isConfirmPage ? (
              <Button
                onClick={() => canAccessBilling()}
                data-cy="manage-button"
              >
                Manage
              </Button>
            ) : (
              priceInfo
            )}
          </StyledPriceInfo>
        )}
      </StyledActivateSection>
      {!isConfirmPage && <Divider />}
    </>
  );
};

const StyledAnsweringName = styled(Text)`
  font-size: 16px;
  line-height: 24px;
`;

const StyledPlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  flex-grow: 1;
`;

const StyledServiceInfo = styled(Text)`
  position: relative;
  font-size: 14px;
  line-height: 22px;
  width: fit-content !important;
`;

const StyledPriceInfo = styled(StyledAnsweringName)`
  margin: 0px 16px;
  font-weight: 600;
  position: relative;
`;

const StyledPriceText = styled.div`
  margin-right: 7px;
`;

const StyledIcon = styled.div`
  margin-left: 5px;
  position: absolute;
  right: -20px;
  top: 50%;
  transform: translateY(-50%);
`;

const StyledTooltipWrapper = styled.div`
  position: absolute;
  right: 0px;
  top: 50%;
`;

export default YourPlanInfo;
